import { useCallback, useEffect, useState } from 'react';

/**
 * Light / Dark / Auto (V6). The choice is the viewer's own and stays in this browser; Auto follows
 * the device and switches live when the device does. The page root carries data-theme, so the
 * console and every drawer or dialog under it follow (theme-dark.css, copied from the design).
 */
export type ThemePref = 'light' | 'dark' | 'auto';

const KEY = 'aithora.theme';
const QUERY = '(prefers-color-scheme: dark)';

export function readThemePref(): ThemePref {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'auto' ? v : 'light';
  } catch {
    return 'light';
  }
}

function deviceDark(): boolean {
  try { return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(QUERY).matches; } catch { return false; }
}

/** Whether a choice means dark right now. Pure apart from the device setting. */
export function isDark(pref: ThemePref, device: boolean = deviceDark()): boolean {
  return pref === 'dark' || (pref === 'auto' && device);
}

export function applyTheme(pref: ThemePref): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', isDark(pref) ? 'dark' : 'light');
}

/** The current choice, a setter that remembers it, and whether the console is dark now. */
export function useTheme(): { pref: ThemePref; setPref: (p: ThemePref) => void; dark: boolean; device: boolean } {
  const [pref, setPrefState] = useState<ThemePref>(readThemePref);
  const [device, setDevice] = useState<boolean>(deviceDark);

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try { mq = window.matchMedia ? window.matchMedia(QUERY) : null; } catch { mq = null; }
    if (!mq) return;
    const on = () => setDevice(mq!.matches);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (typeof mq!.removeEventListener === 'function') mq!.removeEventListener('change', on); else mq!.removeListener(on); };
  }, []);

  useEffect(() => { applyTheme(pref); }, [pref, device]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try { window.localStorage.setItem(KEY, p); } catch { /* private mode: this session only */ }
  }, []);

  return { pref, setPref, dark: isDark(pref, device), device };
}
